import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Brain,
  Lightbulb,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Target,
  Zap,
  Moon,
} from 'lucide-react';
import { HealthMetrics, SurveyData } from '../types';

interface BiohackingInsightsProps {
  healthMetrics: HealthMetrics;
  surveyData: SurveyData[];
}

const BiohackingInsights: React.FC<BiohackingInsightsProps> = ({ healthMetrics, surveyData }) => {
  const generateInsights = () => {
    const insights = [];
    
    // Sleep insights
    if (healthMetrics.sleepQuality < 60) {
      insights.push({
        type: 'warning',
        icon: <Moon />,
        title: 'Sleep Quality Needs Attention',
        description: 'Your sleep quality is below optimal levels. Consider improving your sleep hygiene.',
        suggestions: [
          'Maintain consistent sleep schedule',
          'Avoid screens 1 hour before bed',
          'Keep bedroom cool and dark',
          'Consider magnesium supplementation'
        ]
      });
    } else if (healthMetrics.sleepQuality >= 80) {
      insights.push({
        type: 'success',
        icon: <CheckCircle />,
        title: 'Excellent Sleep Quality',
        description: 'Your sleep quality is excellent! Keep up the great work.',
        suggestions: [
          'Continue your current sleep routine',
          'Track what contributes to good sleep',
          'Share your sleep tips with others'
        ]
      });
    }

    // Energy insights
    if (healthMetrics.energyLevel < 50) {
      insights.push({
        type: 'warning',
        icon: <Zap />,
        title: 'Low Energy Levels',
        description: 'Your energy levels are low. Focus on nutrition and hydration.',
        suggestions: [
          'Increase water intake',
          'Eat more protein and complex carbs',
          'Consider B-vitamin supplementation',
          'Reduce caffeine dependency'
        ]
      });
    }

    // Stress insights
    if (healthMetrics.stressLevel > 70) {
      insights.push({
        type: 'warning',
        icon: <AlertTriangle />,
        title: 'High Stress Levels',
        description: 'Your stress levels are elevated. Focus on stress management.',
        suggestions: [
          'Practice meditation or deep breathing',
          'Engage in regular physical activity',
          'Consider adaptogenic herbs',
          'Prioritize work-life balance'
        ]
      });
    }

    // Activity insights
    if (healthMetrics.physicalActivity < 40) {
      insights.push({
        type: 'warning',
        icon: <Target />,
        title: 'Low Physical Activity',
        description: 'Increase your physical activity for better health outcomes.',
        suggestions: [
          'Start with 10-minute walks',
          'Try high-intensity interval training',
          'Join a fitness class',
          'Use stairs instead of elevators'
        ]
      });
    }

    // Overall performance insights
    if (healthMetrics.overallScore >= 80) {
      insights.push({
        type: 'success',
        icon: <TrendingUp />,
        title: 'Peak Performance',
        description: 'You\'re performing at peak levels! Maintain your current habits.',
        suggestions: [
          'Document your successful routines',
          'Consider advanced biohacking techniques',
          'Share your strategies with others',
          'Set new performance goals'
        ]
      });
    }

    return insights;
  };

  const insights = generateInsights();

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'success':
        return '#00ff88';
      case 'warning':
        return '#ffd700';
      case 'error':
        return '#ff6b6b';
      default:
        return '#4fc3f7';
    }
  };

  const getRecentSupplements = () => {
    const recentSupplements = surveyData.slice(-7).flatMap(s => s.supplements);
    const supplementCounts = recentSupplements.reduce((acc, supplement) => {
      acc[supplement] = (acc[supplement] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(supplementCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
  };

  const getRecentExercises = () => {
    const recentExercises = surveyData.slice(-7)
      .map(s => s.exerciseType)
      .filter(ex => ex && ex !== 'None');
    
    const exerciseCounts = recentExercises.reduce((acc, exercise) => {
      acc[exercise] = (acc[exercise] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return Object.entries(exerciseCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ color: '#00ff88', mb: 3 }}>
          Biohacking Insights
        </Typography>
        
        {insights.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Brain size={48} color="#666" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Complete more surveys to get personalized insights
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
                Personalized Recommendations
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {insights.map((insight, index) => (
                  <Card key={index} sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ color: getInsightColor(insight.type), mt: 0.5 }}>
                          {insight.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2" sx={{ color: getInsightColor(insight.type), mb: 1 }}>
                            {insight.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {insight.description}
                          </Typography>
                          <List dense sx={{ p: 0 }}>
                            {insight.suggestions.map((suggestion, idx) => (
                              <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 20 }}>
                                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: getInsightColor(insight.type) }} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={
                                    <Typography variant="caption" color="text.secondary">
                                      {suggestion}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
                Recent Supplements
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {getRecentSupplements().map(([supplement, count]) => (
                  <Chip
                    key={supplement}
                    label={`${supplement} (${count})`}
                    size="small"
                    sx={{
                      backgroundColor: '#00ff88',
                      color: '#000',
                      fontWeight: 600,
                    }}
                  />
                ))}
                {getRecentSupplements().length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No supplements tracked recently
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#fff' }}>
                Recent Exercises
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {getRecentExercises().map(([exercise, count]) => (
                  <Chip
                    key={exercise}
                    label={`${exercise} (${count})`}
                    size="small"
                    sx={{
                      backgroundColor: '#4fc3f7',
                      color: '#000',
                      fontWeight: 600,
                    }}
                  />
                ))}
                {getRecentExercises().length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No exercises tracked recently
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default BiohackingInsights;
